import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SpaceSelectors, skipper } from '@innovation-os-ng/core/state';
import { EnvironmentService } from '@innovation-os-ng/core/utils';
import { KeyEventData } from '@innovation-os-ng/ui/chart-ng';
import { Store } from '@ngrx/store';
import { Observable, Subject, firstValueFrom, interval, of } from 'rxjs';
import { concatMap, filter, map, switchMap, take } from 'rxjs/operators';
import { KeyEventPostData, NormalizedTimelinePostData } from '../../../../visualization-tab/models/keyEvents.model';
import {
  CountRequest,
  DocumentsQueryPostBody,
  DocumentsQueryResult,
  DocumentsTimeSeriesPostBody,
  DocumentsTimeSeriesResult,
  DocumentsTopicsPostBody,
  DocumentsTopicsResult,
  EntityDetailQueryPostBody,
  EntityDetailQueryPostResult,
  ExecutionDetailStatus,
  NormalizedDataResponse
} from '../../../model/types';
import { QueueService } from './queue.service';

// How often to poll for the status of async aggregation requests (in ms)
const ASYNC_POLLING_INTERVAL = 2000;

@Injectable({
  providedIn: 'root'
})
export class ApiService implements OnDestroy {
  private onDestroy: Subject<void> = new Subject<void>();
  private currentSpaceUri: string;
  constructor(
    private readonly environmentService: EnvironmentService,
    private readonly http: HttpClient,
    private readonly queue: QueueService,
    private readonly store: Store
  ) {
    this.store
      .select(SpaceSelectors.selectCurrentSpaceUri)
      .pipe(skipper(), takeUntilDestroyed())
      .subscribe({
        next: (currentSpaceUri: string) => {
          this.currentSpaceUri = currentSpaceUri;
        }
      });
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  /**
   * Request data from the /documents/query endpoint
   * If data is requested asynchronously, we'll poll every 2s for the status
   * The returned promise will resolve when the async operation is completed
   *
   * @param postBody DocumentsQueryPostBody Contains all configuration for the request
   * @param id string If provided will replace all pending previous calls with the same ID
   *
   * @return Promise that resolves to the query results (if async, it only resolves when the data is ready)
   */
  public postDocumentsQuery(postBody: DocumentsQueryPostBody, id?: string): Promise<DocumentsQueryResult> {
    return new Promise((resolve, reject) => {
      console.assert(!!this.currentSpaceUri, 'currentSpaceUri should not be null')
      const url = this.environmentService.getRestUrl(`/insights-search/insights-search/${this.currentSpaceUri}`);

      const fetchAggregateData = this.http.post<DocumentsQueryResult>(url, postBody);

      this.queue
        .add(fetchAggregateData, id || 'fetchAggregateData', !!id)
        // Here, either pass the completed response through or start polling
        .pipe(switchMap(this.maybePollUntilResultsAreReady(url, { metric: postBody.metric })))
        // Only take the first completed response
        .pipe(take(1))
        .subscribe(
          (res) => {
            resolve(res);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }

  /**
   * Request data from the /insights-search/entities/:spaceUri/details endpoint
   * The returned promise will resolve when the async operation is completed
   *
   * @param postBody EntityDetailQueryPostBody Contains all configuration for the request
   *
   * @return Promise that resolves to the query results (if async, it only resolves when the data is ready)
   */
  public postEntityDetailsQuery(postBody: EntityDetailQueryPostBody): Promise<Array<EntityDetailQueryPostResult>> {
    return new Promise((resolve, reject) => {
      console.assert(!!this.currentSpaceUri, 'currentSpaceUri should not be null')
      const url = this.environmentService.getRestUrl(`/insights-search/entities/${this.currentSpaceUri}/details`);

      const fetchEntityDetailData = this.http.post<Array<EntityDetailQueryPostResult>>(url, postBody);

      this.queue
        .add(fetchEntityDetailData)
        // Only take the first completed response
        .pipe(take(1))
        .subscribe(
          (res) => {
            resolve(res);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }

  /**
   * Fetch the Key Events Growth Rate Data.
   *
   * @param postData - Post data for key events.
   *
   * @returns - Array of key events.
   */
  public getKeyEventsGrowthData(postData: KeyEventPostData): Promise<Array<KeyEventData>> {
    const url: string = this.environmentService.getRestUrl(`/foresight/get_growth`);

    return firstValueFrom(this.http.post<Array<KeyEventData>>(url, postData).pipe(take(1)));
  }

  /**
   * Fetch the normalized timeline data
   * when we Key Events Growth data is enabled.
   *
   * @param postData - Post data for timeline data.
   *
   * @returns - Array of normalized timeline data.
   */
  public getNormalizedTimelineData(postData: NormalizedTimelinePostData): Promise<NormalizedDataResponse> {
    const url: string = this.environmentService.getRestUrl(`/foresight/get_normalized_data`);

    return firstValueFrom(this.http.post<NormalizedDataResponse>(url, postData).pipe(take(1)));
  }

  /**
   * (Outer Function)
   * Builds a function that will map a query response to an Observable that only
   * emits a completed query response (or throws an error if the query failed)
   *
   * (Returned Function)
   * If the response has completed, return it as an Observable
   * If not, start polling and return the response when it is complete
   * This function can operate on DocumentsTopicsResult or DocumentsQueryResult
   *
   * @param url Endpoint to send polling requests to
   * @param postBody Any properties that should be added to the body of the polling request
   * @returns Function(response: T): Observable<T>
   */
  private maybePollUntilResultsAreReady<T extends DocumentsTopicsResult | DocumentsQueryResult>(
    url: string,
    postBody: Partial<DocumentsTopicsPostBody> = {}
  ): (response: T) => Observable<T> {
    return (response: T) => {
      const queryHasCompleted = !(response?.executionDetails?.status === ExecutionDetailStatus.InProgress);
      const completedResponse = queryHasCompleted ? of(response) : this.pollUntilResultsAreReady(response, url, postBody);

      // Map the output so that a failed-status gets thrown as an error
      return completedResponse.pipe(map(this.handlePollingError));
    };
  }

  /**
   * Start polling every 2s for the query's status. The returned Observable will emit only
   * when the request is completed (or failed)
   *
   * @param response An in-progress aggregate query response
   * @param url Endpoint to send polling requests to
   * @param postBody Any properties that should be added to the body of the polling request
   * @returns Observable that only emits when the query is completed
   */
  private pollUntilResultsAreReady<T extends DocumentsQueryResult | DocumentsTopicsResult>(
    response: T,
    url: string,
    postBody: Partial<DocumentsTopicsPostBody> = {}
  ): Observable<T> {
    const asyncId = response.executionDetails.asyncId;
    const statusPostBody = {
      ...postBody,
      executionDetails: { asyncId }
    } as DocumentsTopicsPostBody;

    return interval(ASYNC_POLLING_INTERVAL).pipe(
      concatMap(() => {
        const fetchAggregateStatus = this.http.post<T>(url, statusPostBody);
        return this.queue.add(fetchAggregateStatus);
      }),
      filter((res) => !(res?.executionDetails?.status === ExecutionDetailStatus.InProgress))
    );
  }

  /**
   * Parse the async/polling response to throw an error if the execution status is 'failed'
   * (the QueryExecutionResponseFailed is thrown)
   *
   * @param response A completed (or failed) aggregate query response
   * @returns The input response
   */
  private handlePollingError<T extends DocumentsQueryResult | DocumentsTopicsResult>(response: T): T {
    if (response?.executionDetails?.status === ExecutionDetailStatus.Failed) {
      throw response.executionDetails;
    } else {
      return response;
    }
  }

  /**
   * Request data from the /documents/time-series endpoint
   *
   * @param postBody DocumentsTimeSeriesPostBody Contains all configuration for the request
   * @param id string If provided will replace all pending previous calls with the same ID
   */
  public postDocumentsTimeSeries(postBody: DocumentsTimeSeriesPostBody, id?: string): Promise<DocumentsTimeSeriesResult> {
    return new Promise((resolve, reject) => {
      console.assert(!!this.currentSpaceUri, 'currentSpaceUri should not be null')
      const url = this.environmentService.getRestUrl(
        `/insights-search/insights-search/${this.currentSpaceUri}/time-series`
      );

      const fetchAggregateData = this.http.post<DocumentsTimeSeriesResult>(url, postBody);

      this.queue.add(fetchAggregateData, id || 'fetchTimeSeries', !!id).subscribe(
        (res) => {
          resolve(res);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  /**
   * Request data from the /aggregate/topics
   *
   * @param postBody DocumentsTopicsPostBody Contains all configuration for the request
   * @param id string If provided will replace all pending previous calls with the same ID
   */
  public postDocumentsTopics(postBody: DocumentsTopicsPostBody, id?: string): Promise<DocumentsTopicsResult> {
    return new Promise((resolve, reject) => {
      console.assert(!!this.currentSpaceUri, 'currentSpaceUri should not be null')
      const url = this.environmentService.getRestUrl(
        `/insights-search/insights-search/${this.currentSpaceUri}/aggregate/topics`
      );

      const fetchAggregateData = this.http.post<DocumentsTopicsResult>(url, postBody);

      // Options passed in the polling status request
      const pollingPostBody: Partial<DocumentsTopicsPostBody> = {
        topicSource: postBody.topicSource,
        metric: postBody.metric
      };

      this.queue
        .add(fetchAggregateData, id || 'fetchAggregateTopic', !!id)
        // Here, either pass the completed response through or start polling
        .pipe(switchMap(this.maybePollUntilResultsAreReady(url, pollingPostBody)))
        // Only take the first completed response
        .pipe(take(1))
        .subscribe(
          (res) => {
            resolve(res);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }

  public postSearchFieldDocCount(spaceUri, postBody: { countRequests: Array<CountRequest> }, id?: string) {
    return new Promise((resolve, reject) => {
      const url = `${this.environmentService.getRestUrl()}/insights-search/insights-search/${spaceUri}/count`;

      const fetchAggregateData = this.http.post<DocumentsTopicsResult>(url, postBody);

      this.queue.add(fetchAggregateData, id || 'fetchSfDocCount', !!id).subscribe(
        (res) => {
          resolve(res);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  public getTopicSuggestion(spaceUri: string, body: { property?: string; value?: string }, id?: string) {
    return new Promise((resolve, reject) => {
      const url = `${this.environmentService.getRestUrl()}/insights-search/suggestions/${spaceUri}/values`;

      const fetchAggregateData = this.http.get<DocumentsTopicsResult>(url, {
        params: body as HttpParams
      });

      this.queue.add(fetchAggregateData, id || 'fetchTopicSuggestion', !!id).subscribe(
        (res) => {
          resolve(res);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
}
